import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { SaveWeldTestInput, WeldTest } from "../models/WeldTest";

// Per-slot name: the same device file on both slots would race the server into one path
export const slotPhotoFile = (f: File, slot: number): File => {
	const ext = /\.\w+$/.exec(f.name)?.[0] ?? ".jpg";
	return new File([f], `bend-test-${slot}-${Date.now()}${ext}`, {
		type: f.type,
	});
};

export const uploadWeldTestPhoto = async (file: File): Promise<string> => {
	const api = new MESApiService();
	let response: { result?: { URL?: string }[] };
	try {
		response = await api.uploadFiles([file], "weld-tests");
	} catch (e) {
		// Network-level failures surface from mesf-core as cryptic TypeErrors
		const serverMsg = get(e, "response.data.message", null);
		throw new Error(
			serverMsg
				? `Photo upload failed: ${serverMsg}`
				: "Photo upload failed. Check the connection or log in again, then retry",
		);
	}
	const url = response?.result?.[0]?.URL;
	if (!url)
		throw new Error("Photo upload failed: server did not return a file URL");
	return url;
};

export const saveWeldTest = async (
	input: SaveWeldTestInput,
): Promise<WeldTest | null> => {
	const api = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TestId", value: input.testId ?? null },
		{ name: "AssetId", value: input.assetId },
		{ name: "ProductId", value: input.productId },
		{ name: "BendTest1Passed", value: input.bendTest1Passed },
		{ name: "BendTest2Passed", value: input.bendTest2Passed },
		{ name: "Photo1Path", value: input.photo1Path },
		{ name: "Photo2Path", value: input.photo2Path },
		{ name: "Notes", value: input.notes },
		{ name: "Operator", value: input.operator },
	];
	const resp = await api.callV2("[FAB].[UpsertWeldTest]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error saving weld test");
	return get(resp, "data.tables[0].rows[0]", null) as WeldTest | null;
};

export const deleteWeldTest = async (testId: number) => {
	const api = new MESApiService();
	const parameters: spParameter[] = [{ name: "TestId", value: testId }];
	const resp = await api.callV2("[FAB].[DeleteWeldTest]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error deleting weld test");
	return resp;
};

export const getWeldTests = async (params?: {
	assetId?: number;
	productId?: number;
	shiftId?: number;
	startDate?: string;
	endDate?: string;
}): Promise<WeldTest[]> => {
	const api = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "AssetId", value: params?.assetId ?? null },
		{ name: "ProductId", value: params?.productId ?? null },
		{ name: "ShiftId", value: params?.shiftId ?? null },
		{ name: "StartDate", value: params?.startDate ?? null },
		{ name: "EndDate", value: params?.endDate ?? null },
	];
	const resp = await api.callV2("[FAB].[GetWeldTests]", parameters);
	if (!resp.ok) throw new Error(resp.message || "Error fetching weld tests");
	return get(resp, "data.tables[0].rows", []) as WeldTest[];
};
